import { z } from 'zod';

/**
 * IBAN et BIC du cabinet, imprimés sur la facture pour le virement.
 *
 * Ce ne sont pas des secrets : ils figurent sur chaque facture remise au patient. Ce sont des
 * données du cabinet, validées strictement pour qu'une coquille ne parte pas imprimée sur des
 * centaines de pièces : longueur du pays, caractères permis, clé ISO 13616 (mod 97).
 */

/** Longueurs ISO 13616 des pays où un cabinet luxembourgeois tient vraisemblablement son compte. */
const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  LU: 20,
  BE: 16,
  FR: 27,
  DE: 22,
  NL: 18,
  CH: 21,
  AT: 20,
  IT: 27,
  ES: 24,
  PT: 25,
  MC: 27,
  LI: 21,
  IE: 22,
  GB: 22,
  DK: 18,
  SE: 24,
  FI: 18,
  PL: 28,
};

/** Bornes générales de la norme, pour un pays hors de la table. */
const IBAN_MIN = 15;
const IBAN_MAX = 34;

/** Espaces retirés, majuscules : la forme stockée. */
export function normalizeIban(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

/** Groupes de quatre, la forme imprimée et affichée. */
export function formatIban(value: string): string {
  return normalizeIban(value).replace(/(.{4})/g, '$1 ').trim();
}

function mod97(digits: string): number {
  let remainder = 0;
  for (const char of digits) remainder = (remainder * 10 + Number(char)) % 97;
  return remainder;
}

export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false;
  const expected = IBAN_LENGTHS[iban.slice(0, 2)];
  if (expected !== undefined ? iban.length !== expected : iban.length < IBAN_MIN || iban.length > IBAN_MAX) {
    return false;
  }
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const digits = rearranged.replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
  return mod97(digits) === 1;
}

/** BIC (ISO 9362) : 8 ou 11 caractères. */
export function normalizeBic(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

export function isValidBic(value: string): boolean {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizeBic(value));
}

/** Champ IBAN d'une entrée réseau : vide = aucun, sinon normalisé et vérifié. */
export const ibanFieldSchema = z
  .string()
  .max(50)
  .transform(normalizeIban)
  .refine((value) => value === '' || isValidIban(value), { message: 'invalid_iban' })
  .transform((value) => (value === '' ? null : value))
  .nullable();

/** Champ BIC d'une entrée réseau : facultatif, vide = aucun. */
export const bicFieldSchema = z
  .string()
  .max(20)
  .transform(normalizeBic)
  .refine((value) => value === '' || isValidBic(value), { message: 'invalid_bic' })
  .transform((value) => (value === '' ? null : value))
  .nullable();
